import { useCallback, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import {
	type UseFetchConfigAnalytics,
	useThirdPartyConfig,
} from '@atlassian/rovo-connectors-config/hooks';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

const QUICK_INSERT_EMBEDS_EXPERIENCE_ID = 'confluenceQuickInsertEmbeds';

function toError(value: unknown): Error {
	if (value instanceof Error) {
		return value;
	}
	if (typeof value === 'string') {
		return new Error(value);
	}
	try {
		return new Error(JSON.stringify(value) ?? String(value));
	} catch {
		return new Error(String(value));
	}
}

/**
 * Returns the set of lower-cased third-party product keys the user has an active
 * connection to, sourced live from the search connector config — the same source
 * the Rovo Link Picker relies on.
 *
 * Unlike a passive read of the search-config localStorage cache (which is not
 * populated on the editor route), this hook actively triggers `useThirdPartyConfig`,
 * so it reflects the user's current connection state, including disconnects.
 *
 * A connector counts as "connected" when its config entry has `userNeedsOAuth !== true`
 * (matching the link picker's "usable without an auth prompt" semantics). Connectors
 * the tenant has installed but the user has not authenticated — or has since
 * disconnected — report `userNeedsOAuth: true` and are excluded.
 *
 * Returns `undefined` while the config is still loading (or when `enabled` is false)
 * so callers can fail open and avoid hiding products before the connection state is
 * known.
 *
 * NOTE: This only covers third-party connectors (drive, figma, amplitude, sharepoint,
 * …). First-party / Atlassian products such as Loom never appear in the third-party
 * config and must be handled separately by the caller.
 *
 * @param enabled When false, the hook is fully disabled (no fetch) and returns
 *   `undefined`. Used to keep the request scoped to the experiment cohort.
 */
export function useConnectedThirdPartyProductKeys(enabled: boolean): Set<string> | undefined {
	const { cloudId, userId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireAnalyticsEvent: UseFetchConfigAnalytics['fireAnalyticsEvent'] = useCallback(
		(event) => createAnalyticsEvent(event).fire(),
		[createAnalyticsEvent],
	);

	const logException: UseFetchConfigAnalytics['logException'] = useCallback(
		(exception: unknown) => {
			getMonitoringClient().submitError(toError(exception), {
				attribution: Attribution.INTEGRATIONS,
				partialFailure: true,
			});
		},
		[],
	);

	const analytics: UseFetchConfigAnalytics = useMemo(
		() => ({
			searchSessionId: QUICK_INSERT_EMBEDS_EXPERIENCE_ID,
			source: QUICK_INSERT_EMBEDS_EXPERIENCE_ID,
			actionSubject: 'quickInsertEmbeds',
			taskNamePrefix: QUICK_INSERT_EMBEDS_EXPERIENCE_ID,
			fireAnalyticsEvent,
			logException,
		}),
		[fireAnalyticsEvent, logException],
	);

	const disabled = !enabled || !cloudId || !userId;
	const { thirdPartyConfigs } = useThirdPartyConfig({
		cloudId,
		userId,
		disabled,
		analytics,
	});

	// Derive a stable signature first so the returned Set keeps a stable identity
	// until the connected products actually change. The caller threads this value
	// into a memoised provider list, so a fresh Set on every render would rebuild
	// the editor's extension providers needlessly.
	const signature = useMemo(() => {
		if (disabled || !thirdPartyConfigs) {
			return undefined;
		}
		return Object.entries(thirdPartyConfigs)
			.filter(([, config]) => config?.userNeedsOAuth !== true)
			.map(([key]) => key.toLowerCase())
			.sort()
			.join(',');
	}, [disabled, thirdPartyConfigs]);

	return useMemo(() => {
		if (signature === undefined) {
			return undefined;
		}
		return new Set(signature ? signature.split(',') : []);
	}, [signature]);
}
